// client/src/components/trading-cards/TradingCardDashboard.js
import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';

const TradingCardDashboard = ({ wishlist = false }) => {
  const { isFeatureEnabled } = useFeatureFlags();

  if (!isFeatureEnabled('enableTradingCardsFeature')) {
    return <Navigate to="/" replace />;
  }

  const filterOptions = [
    { value: '', label: 'All Games' },
    { value: 'Pokémon', label: 'Pokémon' },
    { value: 'Magic: The Gathering', label: 'Magic: The Gathering' },
    { value: 'Yu-Gi-Oh!', label: 'Yu-Gi-Oh!' },
    { value: 'Other', label: 'Other' },
  ];

  const renderProps = {
    getImageUrl: (item) => item.imageUrl,
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      const parts = [];
      if (item.game) parts.push(item.game);
      if (item.setName) parts.push(item.setName);
      if (item.cardNumber) parts.push(`#${item.cardNumber}`);
      if (item.rarity) parts.push(item.rarity);
      let base = parts.join(' · ');
      if (item.isGraded && item.gradingCompany && item.grade) {
        base += ` · ${item.gradingCompany} ${item.grade}`;
      }
      return base || item.game || '';
    },
    getItemDetails: (item) => {
      const parts = [`Condition: ${item.condition || 'Near Mint'}`];
      if (item.quantity > 1) parts.push(`Qty: ${item.quantity}`);
      if (item.language && item.language !== 'English') parts.push(item.language);
      if (item.artist) parts.push(`Art: ${item.artist}`);
      return parts.join(' · ');
    },
  };

  const getFilterValue = (item) => item.game;

  const editFields = [
    { name: 'name', label: 'Card Name', type: 'text', required: true, gridProps: { xs: 12 } },
    {
      name: 'game', label: 'Game', type: 'select', required: true,
      options: [
        { value: 'Pokémon', label: 'Pokémon' },
        { value: 'Magic: The Gathering', label: 'Magic: The Gathering' },
        { value: 'Yu-Gi-Oh!', label: 'Yu-Gi-Oh!' },
        { value: 'Other', label: 'Other' },
      ],
      gridProps: { xs: 12, sm: 6 },
    },
    { name: 'setName', label: 'Set Name', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'setCode', label: 'Set Code', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'cardNumber', label: 'Card Number', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'rarity', label: 'Rarity', type: 'text', gridProps: { xs: 12, sm: 6 } },
    {
      name: 'condition', label: 'Condition', type: 'select',
      options: [
        { value: 'Near Mint', label: 'Near Mint' },
        { value: 'Lightly Played', label: 'Lightly Played' },
        { value: 'Moderately Played', label: 'Moderately Played' },
        { value: 'Heavily Played', label: 'Heavily Played' },
        { value: 'Damaged', label: 'Damaged' },
      ],
      gridProps: { xs: 12, sm: 6 },
    },
    { name: 'language', label: 'Language', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'quantity', label: 'Quantity', type: 'number', gridProps: { xs: 12, sm: 6 } },
    // Pokémon-specific
    { name: 'isFirstEdition', label: '1st Edition', type: 'checkbox', showWhen: (d) => d.game === 'Pokémon', gridProps: { xs: 12, sm: 6 } },
    { name: 'isHolo', label: 'Holographic', type: 'checkbox', showWhen: (d) => d.game === 'Pokémon', gridProps: { xs: 12, sm: 6 } },
    // MTG-specific
    {
      name: 'finish', label: 'Finish', type: 'select',
      options: ['Regular', 'Foil', 'Etched', 'Showcase', 'Borderless', 'Extended Art'].map(v => ({ value: v, label: v })),
      showWhen: (d) => d.game === 'Magic: The Gathering',
      gridProps: { xs: 12, sm: 6 },
    },
    // General
    { name: 'type', label: 'Type', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'artist', label: 'Artist', type: 'text', gridProps: { xs: 12, sm: 6 } },
    // Graded
    { name: 'isGraded', label: 'Graded Card', type: 'checkbox', gridProps: { xs: 12, sm: 6 } },
    { name: 'gradingCompany', label: 'Grading Company', type: 'text', showWhen: (d) => !!d.isGraded, gridProps: { xs: 12, sm: 6 } },
    { name: 'grade', label: 'Grade', type: 'text', showWhen: (d) => !!d.isGraded, gridProps: { xs: 12, sm: 6 } },
    // Standard
    { name: 'imageUrl', label: 'Image URL', type: 'text', gridProps: { xs: 12 } },
    { name: 'price', label: 'Price Paid', type: 'text', gridProps: { xs: 12, sm: 6 } },
    { name: 'rating', label: 'Rating', type: 'rating', gridProps: { xs: 12 } },
    { name: 'notes', label: 'Notes', type: 'textarea', gridProps: { xs: 12 } },
  ];

  const tableColumns = {
    name:       { label: 'Card', sortable: true },
    game:       { label: 'Game', sortable: true },
    setName:    { label: 'Set', sortable: true },
    cardNumber: { label: '#', sortable: false },
    rarity:     { label: 'Rarity', sortable: true },
    condition:  { label: 'Condition', sortable: true },
    quantity:   { label: 'Qty', sortable: true, numeric: true },
    price:      { label: 'Price', sortable: true, numeric: true },
  };

  return (
    <CollectionPage
      collectionType={wishlist ? 'trading-cards-wishlist' : 'trading-cards'}
      title={wishlist ? 'Trading Cards Wishlist' : 'Trading Cards Collection'}
      renderProps={renderProps}
      filterOptions={filterOptions}
      getFilterValue={getFilterValue}
      editFields={editFields}
      tableColumns={tableColumns}
    />
  );
};

export default TradingCardDashboard;
